import { useCallback, useRef, useEffect } from 'react';
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { API_URL } from '../config/api.js';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import './ChatKitPanel.css';

/**
 * Domain key for ChatKit domain verification.
 * - Production: VITE_CHATKIT_API_DOMAIN_KEY must be set in CI (GitHub Secret).
 * - Dev (localhost): falls back to built-in dev key when not in production.
 * @see https://platform.openai.com/settings/organization/security/domain-allowlist
 */
const RAW_DOMAIN_KEY = import.meta.env.VITE_CHATKIT_API_DOMAIN_KEY;
const IS_PROD = import.meta.env.MODE === 'production';

let CHATKIT_DOMAIN_KEY;

if (!RAW_DOMAIN_KEY || RAW_DOMAIN_KEY.trim() === '') {
  if (IS_PROD) {
    // Warn loudly in production so misconfigured deploys are noticed in console.

    console.error(
      '[ChatKit] VITE_CHATKIT_API_DOMAIN_KEY is missing in a production build. ' +
        'Set the GitHub Secret and redeploy.'
    );
  }
  CHATKIT_DOMAIN_KEY = 'domain_pk_localhost_dev';
} else {
  CHATKIT_DOMAIN_KEY = RAW_DOMAIN_KEY;
}

/**
 * ChatKitPanel — OpenAI ChatKit integration for the interview agent.
 * Replaces the custom ChatPanel with ChatKit's built-in markdown rendering,
 * streaming visualization, and tool call display.
 */
function ChatKitPanel() {
  const { language } = useTranslation();
  const languageRef = useRef(language);
  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  const authFetch = useCallback(async (url, options = {}) => {
    const authToken = sessionStorage.getItem('interviewAuth') || '';
    const headers = new Headers(options.headers || undefined);
    headers.set('Authorization', `Bearer ${authToken}`);
    headers.set('X-Locale', languageRef.current);

    // Inject locale into JSON body for ChatKit thread metadata
    let body = options.body;
    if (body && typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed && typeof parsed === 'object') {
          if (!parsed.thread) parsed.thread = {};
          if (!parsed.thread.metadata) parsed.thread.metadata = {};
          parsed.thread.metadata.locale = languageRef.current;
          body = JSON.stringify(parsed);
        }
      } catch {
        // Not JSON, leave body unchanged
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
      body,
    });

    if (response.status === 401) {
      sessionStorage.removeItem('interviewAuth');
      window.location.reload();
      return response;
    }

    return response;
  }, []);

  const { control } = useChatKit({
    api: {
      url: `${API_URL}/api/interview/chatkit`,
      fetch: authFetch,
      domainKey: CHATKIT_DOMAIN_KEY,
    },
    // Surface ChatKit lifecycle events for debugging
    onReady: () => {
      // eslint-disable-next-line no-console
      console.log('[ChatKit] Ready -- iframe loaded and initialized');
    },
    onError: (detail) => {
      console.error('[ChatKit] Error:', detail?.error || detail);
    },
    onLog: (detail) => {
      // Gate verbose logging behind dev mode to avoid leaking PII in production
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug('[ChatKit] Log:', detail);
      }
    },
  });

  return (
    <div className="chatkit-panel">
      <ChatKit control={control} />
    </div>
  );
}

export default ChatKitPanel;
